import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useCreateSampleRequest } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, CheckCircle2 } from "lucide-react";

/**
 * Formulario de "Request a custom sample".
 *
 * POR QUE EXISTE. El CTA principal del sitio no capturaba nada: hacia scroll a
 * una seccion informativa. En toda la home no habia un solo campo de email,
 * con una campana de outbound pagada por detras y 31 respuestas recibidas. Lo
 * reporto Laura el 19-08 ("I tried the Request a custom sample button and
 * nothing opened for me").
 *
 * POR QUE PIDE EL ICP TROCEADO Y NO SOLO UN MENSAJE. De esas 31 respuestas, 20
 * son un "Ok" o un "Send it" pelado: el interes llega, el briefing no. Estos
 * campos son las preguntas que de todos modos habria que hacer despues por
 * correo. Todos son OPCIONALES menos el email — un formulario que exige diez
 * campos no lo rellena nadie, y media solicitud vale mas que ninguna.
 */

interface Ctx {
  abrir: (origen?: string) => void;
}
const SampleRequestCtx = createContext<Ctx | null>(null);

/** Abre el formulario desde cualquier CTA. */
export function useSampleRequest(): Ctx {
  const ctx = useContext(SampleRequestCtx);
  // Sin proveedor no se rompe nada: el boton simplemente no abre. Evita que un
  // CTA suelto en una pagina nueva tire la aplicacion entera.
  return ctx ?? { abrir: () => {} };
}

const VACIO = {
  email: "",
  name: "",
  company: "",
  marketplace: "",
  revenue_band: "",
  categories: "",
  roles: "",
  volume: "",
  message: "",
  website: "", // honeypot
};

export function SampleRequestProvider({ children }: { children: React.ReactNode }) {
  const [abierto, setAbierto] = useState(false);
  const [origen, setOrigen] = useState<string>("");
  const [campos, setCampos] = useState(VACIO);
  const [enviado, setEnviado] = useState(false);

  const abrir = useCallback((desde?: string) => {
    setOrigen(desde ?? window.location.pathname);
    setEnviado(false);
    setAbierto(true);
  }, []);

  const cerrar = useCallback(() => setAbierto(false), []);

  // Esc cierra, y mientras esta abierto no se hace scroll detras.
  useEffect(() => {
    if (!abierto) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
    document.addEventListener("keydown", esc);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = overflow;
    };
  }, [abierto, cerrar]);

  const enviar = useCreateSampleRequest({
    mutation: {
      onSuccess: () => {
        setEnviado(true);
        setCampos(VACIO);
      },
    },
  });

  const set = (k: keyof typeof VACIO) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setCampos((c) => ({ ...c, [k]: e.target.value }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = Number.parseInt(campos.volume, 10);
    enviar.mutate({
      data: {
        email: campos.email.trim(),
        ...(campos.name.trim() ? { name: campos.name.trim() } : {}),
        ...(campos.company.trim() ? { company: campos.company.trim() } : {}),
        ...(campos.marketplace.trim() ? { marketplace: campos.marketplace.trim() } : {}),
        ...(campos.revenue_band.trim() ? { revenue_band: campos.revenue_band.trim() } : {}),
        ...(campos.categories.trim() ? { categories: campos.categories.trim() } : {}),
        ...(campos.roles.trim() ? { roles: campos.roles.trim() } : {}),
        ...(Number.isFinite(v) && v > 0 ? { volume: v } : {}),
        ...(campos.message.trim() ? { message: campos.message.trim() } : {}),
        ...(campos.website ? { website: campos.website } : {}),
        source_path: origen,
      },
    });
  };

  return (
    <SampleRequestCtx.Provider value={{ abrir }}>
      {children}
      {abierto ? (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-10 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-muestra"
          data-note-skip=""
          onClick={(e) => { if (e.target === e.currentTarget) cerrar(); }}
        >
          <div className="relative w-full max-w-lg rounded-xl border border-white/10 bg-[#0F1017] p-6 text-white shadow-2xl">
            <button
              type="button"
              onClick={cerrar}
              aria-label="Close"
              className="absolute right-4 top-4 text-white/50 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            {enviado ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-emerald-400" />
                <h2 id="titulo-muestra" className="text-xl font-semibold">Got it — thanks.</h2>
                <p className="mt-2 text-sm text-white/60">
                  We&apos;ll come back to you with a sample built to your brief.
                  Usually within 24 hours.
                </p>
                <Button className="mt-6" onClick={cerrar}>Close</Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <h2 id="titulo-muestra" className="text-xl font-semibold">Request a custom sample</h2>
                  <p className="mt-1 text-sm text-white/60">
                    Tell us who you want to reach and we&apos;ll build a free
                    50-record sample. Only the email is required.
                  </p>
                </div>

                {/* Honeypot: invisible para una persona, irresistible para un
                    bot. `tabIndex={-1}` y `autoComplete="off"` para que ni el
                    teclado ni el autorrelleno del navegador lo toquen. */}
                <div className="absolute left-[-9999px] top-0" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={campos.website}
                    onChange={set("website")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sr-email">Work email *</Label>
                  <Input id="sr-email" type="email" required autoFocus
                         value={campos.email} onChange={set("email")}
                         placeholder="you@agency.com" />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-name">Name</Label>
                    <Input id="sr-name" value={campos.name} onChange={set("name")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-company">Company</Label>
                    <Input id="sr-company" value={campos.company} onChange={set("company")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-market">Marketplace / country</Label>
                    <Input id="sr-market" value={campos.marketplace} onChange={set("marketplace")}
                           placeholder="Amazon US, Germany…" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-revenue">Revenue band</Label>
                    <Input id="sr-revenue" value={campos.revenue_band} onChange={set("revenue_band")}
                           placeholder="$1M–$10M / year" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-cats">Categories</Label>
                    <Input id="sr-cats" value={campos.categories} onChange={set("categories")}
                           placeholder="Home &amp; Kitchen…" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sr-roles">Target roles</Label>
                    <Input id="sr-roles" value={campos.roles} onChange={set("roles")}
                           placeholder="Head of Ecommerce…" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sr-volume">How many records?</Label>
                  <Input id="sr-volume" type="number" min={1} inputMode="numeric"
                         value={campos.volume} onChange={set("volume")} placeholder="50" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sr-msg">Anything else?</Label>
                  <Textarea id="sr-msg" rows={3} value={campos.message} onChange={set("message")}
                            placeholder="Brand-registered sellers only, no resellers…" />
                </div>

                {enviar.isError ? (
                  <p className="text-sm text-red-400">
                    That didn&apos;t go through. Try again, or email{" "}
                    <a className="underline" href="mailto:hello@leadsweet.com">hello@leadsweet.com</a>.
                  </p>
                ) : null}

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={cerrar}>Cancel</Button>
                  <Button type="submit" disabled={enviar.isPending || campos.email.trim() === ""}>
                    {enviar.isPending ? "Sending…" : "Request sample"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </SampleRequestCtx.Provider>
  );
}
